import { useCallback, useContext, useEffect, useState } from 'react';

import * as EmpresaAPI from '../../../api/empresa';
import * as UnidadeCondominialPessoaAPI from '../../../api/unidadeCondominialPessoa';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import styles from '../../form/Form.module.css';

import Form from './Form';

function criarEstadoInicial(empresaId) {
    return {
        empresaId: Number(empresaId) || '',
        unidadeCondominialId: null,
        pessoaId: null,
        UnidadeCondominial: { descricao: '' },
        Pessoa: { descricao: '' },
        dataInicio: '',
        dataFim: '',
        proprietario: false,
        responsavelFinanceiro: false,
        morador: false,
        inquilino: false,
        ativo: true,
        observacao: '',
    };
}

function CreateOrEdit() {
    const { parametro } = useContext(Context);
    const { empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const pathname = '/UnidadeCondominialPessoas';
    const urlParams = new URLSearchParams(window.location.search);

    const uuid = urlParams.get('uuid');
    const editando = Boolean(uuid && uuid !== 'null');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState(() => criarEstadoInicial(empresaId));
    const [optionsEmpresa, setOptionsEmpresa] = useState([]);
    const [carregando, setCarregando] = useState(editando);
    const [salvando, setSalvando] = useState(false);
    const [falhaCarregamento, setFalhaCarregamento] = useState(false);

    const carregarVinculo = useCallback(async () => {
        if (!editando || !uuid) return null;

        setCarregando(true);
        setFalhaCarregamento(false);

        try {
            const response = await UnidadeCondominialPessoaAPI.consultar(uuid);
            const vinculo = response.data?.data || response.data;

            setDados((anterior) => ({
                ...anterior,
                ...vinculo,
                empresaId: Number(vinculo.empresaId) || '',
                unidadeCondominialId: Number(vinculo.unidadeCondominialId) || null,
                pessoaId: Number(vinculo.pessoaId) || null,
                UnidadeCondominial: {
                    ...(vinculo.UnidadeCondominial || {}),
                },
                Pessoa: vinculo.Pessoa || { descricao: '' },
                dataInicio: vinculo.dataInicio ? String(vinculo.dataInicio).slice(0, 10) : '',
                dataFim: vinculo.dataFim ? String(vinculo.dataFim).slice(0, 10) : '',
                observacao: vinculo.observacao || '',
            }));

            return vinculo;
        } catch (error) {
            setFalhaCarregamento(true);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível carregar o vínculo entre a pessoa e a unidade.',
                error.response?.data?.type || 'error'
            );

            return null;
        } finally {
            setCarregando(false);
        }
    }, [editando, setFlashMessage, uuid]);

    const consultarEmpresas = useCallback(async () => {
        const result = await EmpresaAPI.empresa();

        setOptionsEmpresa(result);
        return result;
    }, []);

    useEffect(() => {
        consultarEmpresas();
    }, [consultarEmpresas]);

    useEffect(() => {
        if (!token) navigate('/Auth', { replace: true });
    }, [navigate, token]);

    useEffect(() => {
        carregarVinculo();
    }, [carregarVinculo]);

    async function salvarVinculo(novosDados) {
        if (salvando) return;

        const empresaIdSelecionada = Number(novosDados.empresaId);
        const unidadeCondominialId = Number(novosDados.unidadeCondominialId);
        const pessoaId = Number(novosDados.pessoaId);
        const dataInicio = String(novosDados.dataInicio || '').trim();
        const dataFim = String(novosDados.dataFim || '').trim() || null;

        if (!Number.isInteger(empresaIdSelecionada) || empresaIdSelecionada <= 0) {
            setFlashMessage('Selecione uma empresa válida.', 'warning');
            return;
        }

        if (!Number.isInteger(unidadeCondominialId) || unidadeCondominialId <= 0) {
            setFlashMessage('Selecione uma unidade condominial válida.', 'warning');
            return;
        }

        if (!Number.isInteger(pessoaId) || pessoaId <= 0) {
            setFlashMessage('Selecione uma pessoa válida.', 'warning');
            return;
        }

        if (!novosDados.proprietario && !novosDados.responsavelFinanceiro && !novosDados.morador) {
            setFlashMessage('Informe ao menos uma função da pessoa na unidade.', 'warning');
            return;
        }

        if (novosDados.inquilino && !novosDados.morador) {
            setFlashMessage('A marcação de inquilino exige que a pessoa também seja moradora.', 'warning');
            return;
        }

        if (!dataInicio) {
            setFlashMessage('Informe a data de início do vínculo.', 'warning');
            return;
        }

        if (dataFim && dataFim < dataInicio) {
            setFlashMessage('A data de término não pode ser anterior à data de início.', 'warning');
            return;
        }

        const payload = {
            empresaId: empresaIdSelecionada,
            unidadeCondominialId,
            pessoaId,
            dataInicio,
            dataFim,
            proprietario: Boolean(novosDados.proprietario),
            responsavelFinanceiro: Boolean(novosDados.responsavelFinanceiro),
            morador: Boolean(novosDados.morador),
            inquilino: Boolean(novosDados.inquilino),
            ativo: Boolean(novosDados.ativo),
            observacao: String(novosDados.observacao || '').trim() || null,
        };

        setSalvando(true);

        try {
            const response = editando
                ? await UnidadeCondominialPessoaAPI.editar(uuid, payload)
                : await UnidadeCondominialPessoaAPI.cadastrar(payload);

            setFlashMessage(response.data.message, response.data.type);

            const sucesso = response.status >= 200 && response.status < 300 && response.data?.type === 'success';

            if (!sucesso) return;

            const params = new URLSearchParams(urlParams);

            params.delete('uuid');
            params.delete('btnText');

            navigate(pathname, params);
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || error.message || 'Não foi possível salvar o vínculo entre a pessoa e a unidade.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setSalvando(false);
        }
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                dados={dados}
                setDados={setDados}
                handleSubmit={salvarVinculo}
                caminhoVoltar={pathname}
                OptionsEmpresa={optionsEmpresa}
                editando={editando}
                carregando={carregando}
                salvando={salvando}
                falhaCarregamento={falhaCarregamento}
            />
        </section>
    );
}

export default CreateOrEdit;
